import React, {useMemo, useState, useEffect, useRef} from "react";
import {Col, Container, Row, Card, ListGroup, Accordion, Badge, Button, Alert, Spinner, Form} from 'react-bootstrap';

import {useTranslation} from "react-i18next";
import {usersList} from "../../data";
import {mockStores} from "../../data/stores";
import BreadCrumb from "../../Common/BreadCrumb";
import {capitalizeFirstLetter} from "../../utils/textUtils";
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ChangePassword from "./Components/ChangePassword";
import { dashboardAPI } from "../../services/api";
import martTechLogo from "../../assets/images/martTech-logo.png";


const UserProfile = () => {
    const {t} = useTranslation();
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [currentUserData, setCurrentUserData] = useState<any>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [usernameValue, setUsernameValue] = useState('');
    const [savingUsername, setSavingUsername] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [backendStores, setBackendStores] = useState<any[]>([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const storesFetchedRef = useRef(false);
    
    // Check if demo user - memoized to prevent unnecessary re-renders
    const isDemoUser = useMemo(() => {
        const user = sessionStorage.getItem("authUser");
        if (!user) return false;
        try {
            const userData = JSON.parse(user);
            const token = userData.token || userData.access_token;
            return token && typeof token === 'string' && token.startsWith('demo-token-');
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        const fetchUserData = async () => {
            if (isDemoUser) {
                // Use mock data for demo users
                const mockUser: any = usersList.filter((user: any) => user.id === 0)[0];
                setCurrentUserData(mockUser);
                setUsernameValue(mockUser?.username || mockUser?.email || '');
                return;
            }

            try {
                // Fetch profile from /profile endpoint (includes image)
                const profileResponse: any = await dashboardAPI.getProfile();
                // Normalize response - handle both AxiosResponse and direct data
                const profile = profileResponse?.data || profileResponse;
                setCurrentUserData(profile);
                setUsernameValue(profile?.username || profile?.email || '');
            } catch (err) {
                console.error('Error fetching user profile:', err);
                // Fallback: try getCurrentUser
                try {
                    const userResponse: any = await dashboardAPI.getCurrentUser();
                    // Normalize response - handle both AxiosResponse and direct data
                    const user = userResponse?.data || userResponse;
                    setCurrentUserData(user);
                    setUsernameValue(user?.username || user?.email || '');
                } catch {
                    // Fallback to mock data on error
                    const mockUser: any = usersList.filter((user: any) => user.id === 0)[0];
                    setCurrentUserData(mockUser);
                    setUsernameValue(mockUser?.username || mockUser?.email || '');
                }
            }
        };

        fetchUserData();
    }, [isDemoUser]);

    const currentUser = currentUserData || usersList.filter((user: any) => user.id === 0)[0];
    document.title = t('Profile.title');

    // Get profile picture source
    const getProfileImageSrc = () => {
        if (currentUser?.image) {
            // If image is base64, add data URL prefix if not present
            if (currentUser.image.startsWith('data:')) {
                return currentUser.image;
            }
            return `data:image/jpeg;base64,${currentUser.image}`;
        }
        // Fallback to memberImage or MartTech logo
        return currentUser?.memberImage || martTechLogo;
    };

    // Handle file selection and convert to base64
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError(t('Profile.invalid_image_type') || 'Please select a valid image file');
            return;
        }

        // Validate file size (2MB = 2 * 1024 * 1024 bytes)
        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
            setError(t('Profile.image_too_large') || 'Image size must be less than 2MB');
            return;
        }

        setUploading(true);
        setError(null);
        setSuccess(false);
        setSuccessMessage(null);

        try {
            // Convert to base64
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    const base64String = reader.result as string;
                    // Remove data URL prefix if present
                    const base64Data = base64String.includes(',') 
                        ? base64String.split(',')[1] 
                        : base64String;

                    // Update profile
                    await dashboardAPI.updateProfile({ image: base64Data });
                    
                    // Update local state
                    setCurrentUserData({ ...currentUserData, image: base64Data });
                    setSuccessMessage(t('Profile.upload_success') || 'Profile picture updated successfully!');
                    setSuccess(true);
                    setTimeout(() => {
                        setSuccess(false);
        setSuccessMessage(null);
                        setSuccessMessage(null);
                    }, 3000);
                } catch (err: any) {
                    console.error('Error uploading profile picture:', err);
                    setError(err?.response?.data?.message || err?.message || t('Profile.upload_error') || 'Failed to upload profile picture');
                } finally {
                    setUploading(false);
                    // Reset file input
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                }
            };
            reader.onerror = () => {
                setError(t('Profile.read_error') || 'Failed to read image file');
                setUploading(false);
            };
            reader.readAsDataURL(file);
        } catch (err: any) {
            console.error('Error processing image:', err);
            setError(err?.message || t('Profile.upload_error') || 'Failed to upload profile picture');
            setUploading(false);
        }
    };

    const handleRemovePicture = async () => {
        if (!window.confirm(t('Profile.remove_picture_confirm') || 'Are you sure you want to remove your profile picture?')) {
            return;
        }

        setUploading(true);
        setError(null);
        setSuccess(false);
        setSuccessMessage(null);

        try {
            await dashboardAPI.updateProfile({ image: null });
            setCurrentUserData({ ...currentUserData, image: null });
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err: any) {
            console.error('Error removing profile picture:', err);
            setError(err?.response?.data?.message || err?.message || t('Profile.remove_error') || 'Failed to remove profile picture');
        } finally {
            setUploading(false);
        }
    };

    const handleSaveUsername = async () => {
        const trimmedUsername = usernameValue.trim();
        
        // Validation
        if (!trimmedUsername) {
            setError(t('Profile.username_required') || 'Username is required');
            return;
        }
        
        if (trimmedUsername.length < 3 || trimmedUsername.length > 50) {
            setError(t('Profile.username_length') || 'Username must be between 3 and 50 characters');
            return;
        }
        
        if (!/^[a-zA-Z0-9._-]+$/.test(trimmedUsername)) {
            setError(t('Profile.username_invalid') || 'Username can only contain letters, numbers, dots, underscores, or hyphens');
            return;
        }
        
        if (trimmedUsername === currentUser?.username) {
            setIsEditingUsername(false);
            return;
        }

        setSavingUsername(true);
        setError(null);
        setSuccess(false);
        setSuccessMessage(null);

        try {
            await dashboardAPI.updateProfile({ username: trimmedUsername });
            setCurrentUserData({ ...currentUserData, username: trimmedUsername });
            setSuccessMessage(t('Profile.username_update_success') || 'Username updated successfully! You can use your new username next time you log in.');
            setSuccess(true);
            setIsEditingUsername(false);
            setTimeout(() => {
                setSuccess(false);
        setSuccessMessage(null);
                setSuccessMessage(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error updating username:', err);
            const errorMsg = err?.response?.data?.message || err?.message || t('Profile.username_update_error') || 'Failed to update username';
            setError(errorMsg);
        } finally {
            setSavingUsername(false);
        }
    };

    const handleCancelUsernameEdit = () => {
        setUsernameValue(currentUser?.username || currentUser?.email || '');
        setIsEditingUsername(false);
        setError(null);
    };


    // Fetch stores from backend for real users (only once)
    useEffect(() => {
        // Skip if demo user or already fetched
        if (isDemoUser || storesFetchedRef.current) {
            return;
        }

        const fetchStores = async () => {
            // Mark as fetching to prevent duplicate requests
            storesFetchedRef.current = true;
            setStoresLoading(true);
            try {
                console.log('📦 Fetching stores from backend for profile...');
                const response: any = await dashboardAPI.getStores();
                
                // Normalize response - handle both AxiosResponse and direct data
                const stores = Array.isArray(response) ? response : (response?.data || []);
                
                console.log('✅ Stores received:', stores.length, 'stores');
                setBackendStores(stores);
            } catch (error: any) {
                console.error('❌ Failed to fetch stores:', error);
                setBackendStores([]);
                // Reset flag on error so we can retry if needed
                storesFetchedRef.current = false;
            } finally {
                setStoresLoading(false);
            }
        };

        fetchStores();
    }, [isDemoUser]); // Only depends on isDemoUser, which is memoized

    const userStores = useMemo(() => {
        if (!currentUser) {
            return [];
        }

        let filtered: any[] = [];

        if (isDemoUser) {
            // For demo users, use mock stores filtered by user ID
            filtered = mockStores.filter(store => store.users.includes(currentUser.id));
        } else {
            // For real users, filter backend stores by storeIds from JWT
            const userStoreIds = currentUser.storeIds || [];
            if (userStoreIds.length > 0) {
                filtered = backendStores.filter((store: any) => {
                    const storeId = store.id || store.storeId;
                    return userStoreIds.includes(storeId) || userStoreIds.some((id: any) => id.toString() === storeId?.toString());
                });
            } else {
                // If no storeIds, show all stores (for company admins, etc.)
                filtered = backendStores;
            }
        }

        // Group by city and province
        const grouped: Record<string, { city: string; provinces: Record<string, any[]> }> = {};

        filtered.forEach(store => {
            const city = store.city || '';
            const province = store.province || '';
            
            if (!grouped[city]) {
                grouped[city] = { city: city, provinces: {} };
            }
            if (!grouped[city].provinces[province]) {
                grouped[city].provinces[province] = [];
            }
            grouped[city].provinces[province].push(store);
        });

        return Object.values(grouped);
    }, [currentUser, backendStores, isDemoUser]);


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Profile.page_title'), path: "/profile"}
                        ]}
                    />
                    <Row>
                        <Col lg="12">
                            <Card>
                                <Card.Body>
                                    <ListGroup variant="flush">

                                        <ListGroup.Item className="p-3 p-md-4">
                                            {error && (
                                                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                                                    {error}
                                                </Alert>
                                            )}
                                            {success && successMessage && (
                                                <Alert variant="success" dismissible onClose={() => {
                                                    setSuccess(false);
        setSuccessMessage(null);
                                                    setSuccessMessage(null);
                                                }} className="mb-3">
                                                    {successMessage}
                                                </Alert>
                                            )}
                                            <div className="d-flex flex-column flex-md-row">
                                                <div className="mx-3 mb-3 mb-md-0 position-relative" style={{ alignSelf: 'flex-start' }}>
                                                    <img
                                                        src={getProfileImageSrc()}
                                                        alt="Profile"
                                                        className="avatar-md rounded-circle img-thumbnail"
                                                        style={{ objectFit: 'cover', width: '80px', height: '80px' }}
                                                    />
                                                    {!isDemoUser && (
                                                        <div 
                                                            className="position-absolute"
                                                            style={{
                                                                bottom: '-8px',
                                                                right: '-8px',
                                                                display: 'flex',
                                                                gap: '6px',
                                                                flexDirection: 'row'
                                                            }}
                                                        >
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="rounded-circle shadow-sm"
                                                                onClick={() => fileInputRef.current?.click()}
                                                                disabled={uploading}
                                                                title={t('Profile.change_picture') || 'Change picture'}
                                                                style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    padding: '0',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    border: '2px solid white',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                }}
                                                            >
                                                                <i className="ri-camera-line" style={{ fontSize: '14px' }}></i>
                                                            </Button>
                                                            {currentUser?.image && (
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="rounded-circle shadow-sm"
                                                                    onClick={handleRemovePicture}
                                                                    disabled={uploading}
                                                                    title={t('Profile.remove_picture') || 'Remove picture'}
                                                                    style={{
                                                                        width: '32px',
                                                                        height: '32px',
                                                                        padding: '0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        border: '2px solid white',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                    }}
                                                                >
                                                                    <i className="ri-delete-bin-line" style={{ fontSize: '14px' }}></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleFileSelect}
                                                        style={{ display: 'none' }}
                                                        disabled={uploading}
                                                    />
                                                    {uploading && (
                                                        <div 
                                                            className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: '80px',
                                                                height: '80px',
                                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                                borderRadius: '50%'
                                                            }}
                                                        >
                                                            <Spinner animation="border" size="sm" variant="light" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1 align-self-center ms-0 ms-md-3 mt-2 mt-md-0">
                                                    <div className="text-muted">
                                                        <p className="mb-1 mb-md-0 small">Id no:
                                                            #{currentUser.id || currentUser.userId}</p>
                                                        {currentUser.firstName && currentUser.lastName && (
                                                            <h5 className="mb-1 mb-md-2" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                                                                {`${currentUser.firstName} ${currentUser.lastName}`}
                                                            </h5>
                                                        )}
                                                        {!isDemoUser && isEditingUsername ? (
                                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                                <Form.Control
                                                                    type="text"
                                                                    value={usernameValue}
                                                                    onChange={(e) => setUsernameValue(e.target.value)}
                                                                    disabled={savingUsername}
                                                                    style={{ maxWidth: '250px' }}
                                                                    placeholder={t('Profile.username') || 'Username'}
                                                                />
                                                                <Button
                                                                    variant="success"
                                                                    size="sm"
                                                                    onClick={handleSaveUsername}
                                                                    disabled={savingUsername}
                                                                >
                                                                    {savingUsername ? (
                                                                        <Spinner animation="border" size="sm" />
                                                                    ) : (
                                                                        <i className="ri-check-line"></i>
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    onClick={handleCancelUsernameEdit}
                                                                    disabled={savingUsername}
                                                                >
                                                                    <i className="ri-close-line"></i>
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                    <p className="mb-0">
                                                                        <span className="text-muted small">{t('Profile.username') || 'Username'}: </span>
                                                                        {currentUser?.username || currentUser?.email || 'N/A'}
                                                                    </p>
                                                                    {!isDemoUser && (
                                                                        <Button
                                                                            variant="link"
                                                                            size="sm"
                                                                            className="p-0 text-decoration-none"
                                                                            onClick={() => {
                                                                                setUsernameValue(currentUser?.username || currentUser?.email || '');
                                                                                setIsEditingUsername(true);
                                                                            }}
                                                                            title={t('Profile.edit_username') || 'Edit username'}
                                                                        >
                                                                            <i className="ri-edit-line" style={{ fontSize: '14px' }}></i>
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                <p className="mb-0 small text-muted">
                                                                    <span className="text-muted">{t('Profile.email') || 'Email'}: </span>
                                                                    {currentUser.email || 'N/A'}
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="p-3 p-md-4">
                                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 gap-md-0">
                                                <div className="mb-2 mb-md-0">
                                                    <strong>{t('Profile.email')}:</strong> {currentUser.email || currentUser.username}
                                                </div>
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => setShowChangePassword(true)}
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    <i className="ri-lock-password-line me-1"></i>
                                                    {t('Profile.change_password') || 'Change Password'}
                                                </Button>
                                            </div>
                                        </ListGroup.Item>
                                        <ListGroup.Item className="p-3 p-md-4">
                                            <strong className="d-block mb-3">{t('Profile.stores')}:</strong>
                                            {storesLoading ? (
                                                <div className="text-center py-3">
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    <span className="text-muted">{t('Common.loading') || 'Loading stores...'}</span>
                                                </div>
                                            ) : userStores.length === 0 ? (
                                                <p className="text-muted mb-0">{t('Profile.no_stores') || 'No stores assigned'}</p>
                                            ) : (
                                                <Accordion>
                                                    {userStores.map((group, i) => (
                                                        <Accordion.Item eventKey={i.toString()} key={i}>
                                                            <Accordion.Header>
                                                                {group.city ? capitalizeFirstLetter(group.city) : t('Profile.unknown_city') || 'Unknown City'}
                                                            </Accordion.Header>
                                                            <Accordion.Body>
                                                                <Accordion alwaysOpen>
                                                                    {Object.entries(group.provinces).map(([province, stores], j) => (
                                                                        <Accordion.Item eventKey={`${i}-${j}`} key={j}>
                                                                            <Accordion.Header>
                                                                                {province ? capitalizeFirstLetter(province) : t('Profile.unknown_province') || 'Unknown Province'}
                                                                            </Accordion.Header>
                                                                            <Accordion.Body>
                                                                                <div className="d-flex flex-wrap gap-2">
                                                                                    {stores.map((store: any, k: number) => (
                                                                                        <div key={k} className="d-flex align-items-center gap-1">
                                                                                            <Badge bg="primary" pill>
                                                                                                {store.name || store.storeName || 'Unknown Store'}
                                                                                            </Badge>
                                                                                            {
                                                                                                (store.active !== undefined ? store.active : store.isActive !== false) 
                                                                                                    ? <ToggleOnIcon color="success" />
                                                                                                    : <ToggleOffIcon color="disabled" />
                                                                                            }
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </Accordion.Body>
                                                                        </Accordion.Item>
                                                                    ))}
                                                                </Accordion>
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    ))}
                                                </Accordion>
                                            )}
                                        </ListGroup.Item>


                                    </ListGroup>

                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            
            <ChangePassword 
                show={showChangePassword}
                onHide={() => setShowChangePassword(false)}
                onSuccess={() => {
                    // Optionally refresh user data or show success message
                }}
            />
        </React.Fragment>
    )
}

export default UserProfile;
